import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useState, useEffect } from "react";
import { FileText, Download, Eye, X, Loader2 } from "lucide-react";

import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";

// Compact profile card for the signed advisor-agreement PDF (created by the Sales Portal).
// Self-hiding: renders nothing unless the logged-in advisor has a signed agreement on file.
const AgreementSection = ({ currentUser, onSessionExpired, onTokenRefresh }) => {
  const [meta, setMeta] = useState(null); // { exists, signedAt, formType }
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const apiUrl = getApiUrl();
  const authParams = { currentUser, onSessionExpired, onTokenRefresh };

  // On mount: fetch metadata, then preload the PDF blob so the card can show a
  // thumbnail preview (and View/Download reuse the same cached object URL).
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await authFetch(`${apiUrl}/agreement`, {}, authParams);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setMeta(data);
        if (!data.exists) return;

        const pdfRes = await authFetch(`${apiUrl}/agreement/pdf`, {}, authParams);
        if (cancelled || !pdfRes.ok) return;
        createdUrl = URL.createObjectURL(await pdfRes.blob());
        if (cancelled) {
          URL.revokeObjectURL(createdUrl);
          return;
        }
        setPdfUrl(createdUrl);
      } catch (err) {
        if (!(err instanceof SessionExpiredError)) {
          /* silent — section just stays hidden */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Revoke the object URL when the component unmounts.
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Close the preview modal on Escape.
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen]);

  // Fetch (once) and cache the PDF blob; returns the object URL or null.
  const ensurePdf = async () => {
    if (pdfUrl) return pdfUrl;
    setLoadingPdf(true);
    try {
      const res = await authFetch(`${apiUrl}/agreement/pdf`, {}, authParams);
      if (!res.ok) return null;
      const url = URL.createObjectURL(await res.blob());
      setPdfUrl(url);
      return url;
    } catch (err) {
      if (!(err instanceof SessionExpiredError)) {
        /* silent */
      }
      return null;
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleView = async () => {
    const url = await ensurePdf();
    if (url) setModalOpen(true);
  };

  const handleDownload = async () => {
    const url = await ensurePdf();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = "advisor-agreement.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!meta?.exists) return null;

  const signed = meta.signedAt ? new Date(meta.signedAt).toLocaleDateString() : null;
  const typeLabel = meta.formType === "jg" ? "Johnston Group" : "General";

  // shared sizing so View and Download are identical; only colors differ
  const btnBase =
    "inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-lg h-10 w-32 transition-colors disabled:opacity-60";

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <h3 className="font-semibold text-gray-900">Advisor Agreement</h3>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-4">
            {/* live PDF thumbnail — click to open the full preview */}
            <button
              type="button"
              onClick={handleView}
              title="View agreement"
              className="w-16 h-[84px] flex-shrink-0 rounded-lg border border-gray-200 bg-white overflow-hidden relative group focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {pdfUrl ? (
                <iframe
                  title="Agreement preview"
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  className="border-0 pointer-events-none"
                  style={{
                    width: "256px",
                    height: "336px",
                    transform: "scale(0.25)",
                    transformOrigin: "top left",
                  }}
                />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
                </span>
              )}
              <span className="absolute inset-0 group-hover:bg-primary/5 transition-colors" />
            </button>

            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">{typeLabel} agreement</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {signed ? `Signed ${signed}` : "Signed"} · PDF
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={handleView}
                  disabled={loadingPdf}
                  className={`${btnBase} text-white bg-primary border border-primary hover:bg-primary-dark`}
                >
                  {loadingPdf ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  View
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={loadingPdf}
                  className={`${btnBase} text-gray-700 bg-white border border-gray-200 hover:bg-gray-50`}
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* preview modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-modal w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">Advisor Agreement</h3>
                  <p className="text-xs text-gray-500">
                    {typeLabel}{signed ? ` · Signed ${signed}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:bg-primary/5 rounded-lg px-3 py-1.5 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  aria-label="Close preview"
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-50">
              {pdfUrl && (
                <iframe title="Advisor Agreement" src={pdfUrl} className="w-full h-full border-0" />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
  onSessionExpired: () => dispatch(sessionExpired()),
  onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AgreementSection);
